import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Box,
  LinearProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  People,
  Email,
  Phone,
  Person,
  CheckCircle,
  Warning,
  Info,
  Refresh,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';

interface UserProfileStatsData {
  totalUsers: number;
  profileCompletionStats: {
    complete: number;
    partial: number;
    minimal: number;
  };
  verificationStats: {
    emailVerified: number;
    phoneVerified: number;
    bothVerified: number;
    noneVerified: number;
  };
  fieldCompletionStats: {
    fullName: number;
    email: number;
    phone: number;
  };
  trends: {
    newRegistrations: number;
    profileCompletions: number;
    verifications: number;
  };
}

export const UserProfileStats: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<UserProfileStatsData | null>(null);

  // Mock data for now - replace with actual API call
  const mockStats: UserProfileStatsData = {
    totalUsers: 1247,
    profileCompletionStats: {
      complete: 845,  // 67.8%
      partial: 312,   // 25.0%
      minimal: 90,    // 7.2%
    },
    verificationStats: {
      emailVerified: 923,   // 74.0%
      phoneVerified: 756,   // 60.6%
      bothVerified: 634,    // 50.8%
      noneVerified: 189,    // 15.1%
    },
    fieldCompletionStats: {
      fullName: 1167,  // 93.6%
      email: 1089,     // 87.3%
      phone: 945,      // 75.8%
    },
    trends: {
      newRegistrations: 47,
      profileCompletions: 23,
      verifications: 31,
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // TODO: Replace with actual API call
      // const response = await adminAPI.getUserProfileStats();
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStats(mockStats);
    } catch (error) {
      setError('Failed to load profile statistics');
      console.error('Error fetching profile stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPercentage = (value: number, total: number) => {
    return total > 0 ? Math.round((value / total) * 100) : 0;
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <IconButton size="small" onClick={fetchStats}>
              <Refresh />
            </IconButton>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="User Profile Statistics"
        subheader="Profile completion and verification metrics"
        action={
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchStats} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        }
        avatar={<People color="primary" />}
      />
      
      <CardContent>
        {/* Overview Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                {stats.totalUsers.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {getPercentage(stats.profileCompletionStats.complete, stats.totalUsers)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete Profiles
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 600 }}>
                {getPercentage(stats.verificationStats.bothVerified, stats.totalUsers)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fully Verified
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                <TrendingUp color="success" fontSize="small" />
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                  +{stats.trends.newRegistrations}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                New This Week
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Profile Completion Breakdown */}
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="primary" />
          Profile Completion Breakdown
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Complete (100%)</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.profileCompletionStats.complete} users
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getPercentage(stats.profileCompletionStats.complete, stats.totalUsers)}
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Partial (50-99%)</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.profileCompletionStats.partial} users
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getPercentage(stats.profileCompletionStats.partial, stats.totalUsers)}
                color="warning"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Minimal (&lt;50%)</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.profileCompletionStats.minimal} users
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getPercentage(stats.profileCompletionStats.minimal, stats.totalUsers)}
                color="error"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Field Completion Stats */}
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="primary" />
          Field Completion Rates
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { field: 'fullName', label: 'Full Name', icon: <Person fontSize="small" /> },
            { field: 'email', label: 'Email', icon: <Email fontSize="small" /> },
            { field: 'phone', label: 'Phone', icon: <Phone fontSize="small" /> },
          ].map(({ field, label, icon }) => {
            const count = stats.fieldCompletionStats[field as keyof typeof stats.fieldCompletionStats];
            const percentage = getPercentage(count, stats.totalUsers);
            
            return (
              <Grid item xs={12} sm={4} key={field}>
                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    {icon}
                    <Typography variant="subtitle2" sx={{ ml: 1 }}>
                      {label}
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                    {percentage}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {count.toLocaleString()} / {stats.totalUsers.toLocaleString()} users
                  </Typography>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Verification Stats */}
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="primary" />
          Verification Status
        </Typography>
        
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Chip
            icon={<Email />}
            label={`Email: ${getPercentage(stats.verificationStats.emailVerified, stats.totalUsers)}%`}
            color={getCompletionColor(getPercentage(stats.verificationStats.emailVerified, stats.totalUsers))}
            variant="outlined"
          />
          <Chip
            icon={<Phone />}
            label={`Phone: ${getPercentage(stats.verificationStats.phoneVerified, stats.totalUsers)}%`}
            color={getCompletionColor(getPercentage(stats.verificationStats.phoneVerified, stats.totalUsers))}
            variant="outlined"
          />
          <Chip
            icon={<CheckCircle />}
            label={`Both: ${getPercentage(stats.verificationStats.bothVerified, stats.totalUsers)}%`}
            color={getCompletionColor(getPercentage(stats.verificationStats.bothVerified, stats.totalUsers))}
            variant="outlined"
          />
          <Chip
            icon={<Warning />}
            label={`None: ${getPercentage(stats.verificationStats.noneVerified, stats.totalUsers)}%`}
            color="error"
            variant="outlined"
          />
        </Stack>

        {/* Trends Alert */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            📈 This week: <strong>+{stats.trends.profileCompletions}</strong> profile completions, 
            <strong> +{stats.trends.verifications}</strong> verifications from <strong>{stats.trends.newRegistrations}</strong> new users
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};